/*
 * zedaro-quick-add.js — quantity + "Add to cart" on a product card, instant.
 *
 * WHY THIS EXISTS RATHER THAN THE THEME'S OWN QUICK ADD
 * Trade ships two quick-add styles and neither fits a trade counter:
 *   'standard' is a single button with no quantity — wrong for a shop where
 *              people order 10 of something, not 1;
 *   'bulk'     is a quantity stepper that debounces and posts BY ITSELF a
 *              moment after the last keystroke. Customers could not tell
 *              whether the item had gone in, so they pressed again.
 * This is the explicit version: type a quantity, press Add to cart, it goes in
 * immediately and the button confirms. Nothing happens until the button is
 * pressed, which is the behaviour people expect from a cart.
 *
 * It is delegated from the document, so cards rendered later — a paginated
 * grid, a section re-render, a search result list — work with no re-binding.
 *
 * After a successful add it publishes the theme's own cart-update event, so the
 * header count, the cart drawer AND zedaro-cart-owner.js (which stamps the cart
 * with its owner) all react exactly as they do for a native add. Without that
 * publish the cart would be updated but left unstamped, and the owner lock
 * would then withhold it — the blank-cart failure again.
 */
(function () {
  'use strict';

  var BUSY = 'is-busy';

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function announce(button, text, revertTo) {
    button.textContent = text;
    if (revertTo) {
      setTimeout(function () {
        button.textContent = revertTo;
        button.classList.remove(BUSY);
        button.disabled = false;
      }, 1400);
    }
  }

  /* Resolve a variant when the button only knows a handle.
     The Typesense search index carries no variant id — adding one would mean a
     schema change and a full reindex of 114k documents — so a search card names
     the product by handle and we ask Shopify for its variants at click time.
     One small request, only when someone actually adds. A product with more
     than one variant cannot be added from a card at all (nothing has chosen a
     variant), so it opens the product page instead. */
  function resolveVariant(handle) {
    return fetch(routeRoot() + 'products/' + encodeURIComponent(handle) + '.js', {
      headers: { 'Accept': 'application/json' }
    }).then(function (r) {
      if (!r.ok) throw new Error('lookup failed');
      return r.json();
    }).then(function (p) {
      var variants = (p && p.variants) || [];
      if (variants.length > 1) return { multiple: true, url: p.url };
      var v = variants[0];
      if (!v || v.available === false) throw new Error('Unavailable');
      /* The same DENY ceiling, learned from the payload we already have, so a
         card rendered before the cap was indexed still cannot oversell. */
      var cap = 0;
      if (v.inventory_management && String(v.inventory_policy).toLowerCase() === 'deny') {
        cap = Math.max(0, parseInt(v.inventory_quantity, 10) || 0);
      }
      return { id: v.id, max: cap };
    });
  }


  /* REDRAW THE HEADER CART ICON IMMEDIATELY.

     Publishing cart-update alone was not enough: that event is what the theme's
     own cart components listen to, and on a page with no cart drawer nothing is
     listening, so the count bubble kept whatever the server rendered until the
     next navigation. The bubble is its own section (sections/cart-icon-bubble),
     so we ask Shopify to re-render just that section and swap it in — the same
     Section Rendering API the native add uses. One tiny request, and the number
     changes the moment the item lands.

     If anything about the section render fails we fall back to /cart.js and
     write the count into the existing bubble by hand, so the number is still
     right even if the markup cannot be replaced. */
  function refreshCartIcon() {
    var icon = document.getElementById('cart-icon-bubble');
    if (!icon) return;

    fetch(routeRoot() + '?sections=cart-icon-bubble', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var html = data && data['cart-icon-bubble'];
        if (!html) throw new Error('no section html');
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.getElementById('cart-icon-bubble');
        if (!fresh) throw new Error('no bubble in section');
        icon.innerHTML = fresh.innerHTML;
      })
      .catch(function () {
        fetch(routeRoot() + 'cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (cart) {
            if (!cart) return;
            var bubble = icon.querySelector('.cart-count-bubble');
            if (bubble) {
              var span = bubble.querySelector('span[aria-hidden="true"]');
              if (span) span.textContent = cart.item_count < 100 ? cart.item_count : '';
            } else if (cart.item_count > 0) {
              var el = document.createElement('div');
              el.className = 'cart-count-bubble';
              el.innerHTML = '<span aria-hidden="true">' + cart.item_count + '</span>';
              icon.appendChild(el);
            }
          })
          .catch(function () { /* leave the icon as it is */ });
      });
  }

  function addToCart(button) {
    if (button.disabled) return;
    var variantId = button.getAttribute('data-variant-id');
    var handle = button.getAttribute('data-handle');
    if (!variantId && handle) {
      var originalLabel = button.getAttribute('data-label') || 'Add to cart';
      button.disabled = true;
      button.classList.add(BUSY);
      announce(button, 'Adding\u2026');
      resolveVariant(handle).then(function (res) {
        if (res.multiple) {
          window.location.href = res.url || ('/products/' + handle);
          return;
        }
        button.setAttribute('data-variant-id', String(res.id));
        /* Set OR CLEAR the ceiling from the live variant (audit ZOPS-125).
           Only setting it when max > 0 meant a stale cap survived: a card
           rendered when the variant was DENY kept that limit after the policy
           was changed to CONTINUE, silently capping an order that no longer
           needed capping. The live answer is authoritative in both directions. */
        var qi = button.closest('.zd-add-wrap');
        qi = qi ? qi.querySelector('.zd-add-qty') : null;
        if (res.max > 0) {
          button.setAttribute('data-max', String(res.max));
          if (qi) qi.setAttribute('max', String(res.max));
        } else {
          button.removeAttribute('data-max');
          if (qi) qi.removeAttribute('max');
        }
        /* data-min is NOT touched here. The minimum comes from the vendor
           pricelist, which /products/<handle>.js does not expose, so the value
           rendered onto the card is the only one we have — clearing it from a
           live lookup that never knew it would silently drop the floor. */
        button.disabled = false;
        button.classList.remove(BUSY);
        addToCart(button);
      }).catch(function (err) {
        announce(button, (err && err.message) || 'Could not add', originalLabel);
      });
      return;
    }
    if (!variantId) return;

    var wrap = button.closest('.zd-add-wrap');
    var input = wrap ? wrap.querySelector('.zd-add-qty') : null;
    var qty = parseInt((input && input.value) || '1', 10);
    if (!qty || qty < 1) qty = 1;

    /* CAP AT WHAT CAN ACTUALLY BE SOLD (2026-08-24).

       A variant whose inventory policy is DENY cannot be oversold, so asking for
       more than is on hand is guaranteed to be refused. Shopify would reject the
       add with a 422 and the button would show its error — which works, but only
       AFTER the customer has typed a number, pressed the button and waited. The
       ceiling is known up front, so enforce it here: clamp the value, put it back
       in the box so the customer sees what will actually be ordered, and say why.

       data-max is absent when there is no cap (CONTINUE policy or untracked), and
       then nothing is clamped — a back-order is legitimate for those. */
    var cap = parseInt(button.getAttribute('data-max') || '0', 10);
    var floor = parseInt(button.getAttribute('data-min') || '1', 10);
    if (!floor || floor < 1) floor = 1;
    var clamped = false;

    /* RAISE TO THE MINIMUM (audit ZOPS-126, 2026-08-25).

       The vendor minimum is enforced HERE as well as on the input, because the
       input is only a hint: a number box can be cleared, and a card rendered
       before this shipped carries no floor at all. Both cards and search
       results come through this one function, so one check covers both — which
       is the whole reason the add path is shared.

       This is still a UI guard, not a boundary: Shopify's /cart/add accepts any
       quantity, and making a minimum unbypassable needs a Cart Validation
       Function. It stops the accident, not a determined request. */
    if (qty < floor) {
      qty = floor;
      clamped = true;
      if (input) input.value = String(floor);
    }

    if (cap > 0 && qty > cap) {
      qty = cap;
      clamped = true;
      if (input) input.value = String(cap);
    }

    var original = button.getAttribute('data-label') || 'Add to cart';
    button.disabled = true;
    button.classList.add(BUSY);
    announce(button, 'Adding…');

    fetch(routeRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw e; });
        return r.json();
      })
      .then(function () {
        announce(button, clamped ? 'Added ' + qty : 'Added \u2713', original);
        refreshCartIcon();
        /* Let the rest of the theme know, exactly as a native add would. */
        try {
          if (typeof publish === 'function' &&
              typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'zedaro-quick-add' });
          }
        } catch (e) { /* the add still succeeded; the count catches up on nav */ }
      })
      .catch(function (err) {
        /* Shopify returns a readable description for the common refusals —
           sold out, quantity rule, unpublished — so show it rather than a
           generic failure the customer cannot act on. */
        var msg = (err && (err.description || err.message)) || 'Could not add';
        announce(button, String(msg).slice(0, 40), original);
      });
  }

  document.addEventListener('click', function (e) {
    var button = e.target.closest ? e.target.closest('.zd-add') : null;
    if (!button) return;
    e.preventDefault();
    addToCart(button);
  });

  /* Enter inside the quantity box adds, rather than submitting a stray form. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var input = e.target.closest ? e.target.closest('.zd-add-qty') : null;
    if (!input) return;
    e.preventDefault();
    var wrap = input.closest('.zd-add-wrap');
    var button = wrap ? wrap.querySelector('.zd-add') : null;
    if (button) addToCart(button);
  });
})();
